import { format } from 'date-fns';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { FeedbackEvent } from '@bonusly/common-feedback';
import { CreateMeetingModal, EditMeetingModal } from '@bonusly/common-meeting-modal';
import {
	Avatar,
	Box,
	CalendarCancelledOutline,
	CalendarOutline,
	ChatAltOutline,
	ClappingHandsOutline,
	CogOutline,
	Divider,
	DotsHorizontalSolid,
	ExternalLinkOutline,
	HistoryOutline,
	HStack,
	IconButton,
	Menu,
	MenuButton,
	MenuItem,
	MenuList,
	PlugOutline,
	Portal,
	rem,
	ShareOutline,
	TargetOutline,
	Text,
	useDisclosure,
	useToast,
	VStack,
} from '@bonusly/design-system';
import { useDispatch } from '@bonusly/events';
import { Permission, ProtectedContent, usePermission } from '@bonusly/who-am-i';

import { CreateReviewModal } from '../../../components/growth/review-recap/components/create-review-modal';
import { GiveBoxEvent } from '../../../components/recognition/give-box/events';
import { DirectReportEntity } from '../../../entities/user';
import { useCreateGoalModal } from '../../../modules/growth/components/goals';
import { useCurrentOneOnOneWithReport } from '../../../modules/growth/components/one-on-one-meeting';
import { CancelMeetingModal } from '../../growth/components/one-on-one-meeting/components/cancel-modal/cancel-meeting-modal';
import { ReactComponent as GoogleCalendar } from '../../growth/components/one-on-one-meeting/components/meeting/assets/google-calendar.svg';
import { ReactComponent as Outlook } from '../../growth/components/one-on-one-meeting/components/meeting/assets/outlook.svg';
import { RescheduleModal } from '../../growth/components/one-on-one-meeting/components/reschedule-modal/reschedule-modal';
import { useCurrentUserRole } from '../../growth/components/one-on-one-meeting/hooks/use-current-user-role';
import { UserNavLink } from './user-nav-link';

interface DirectReportMenuProps {
	directReport: DirectReportEntity;
}

export const DirectReportMenu: FC<DirectReportMenuProps> = ({ directReport }) => {
	const { t } = useTranslation(['navigation', 'one_on_ones', 'common']);
	const canAccessGoals = usePermission(Permission.CanAccessGoals);
	const dispatch = useDispatch();
	const toast = useToast();

	// Meeting data
	const { data: meeting, isLoading: isLoadingMeeting } = useCurrentOneOnOneWithReport(directReport.id);
	const event = meeting?.event;
	const participant = event?.guest;
	const hasOneOnOne = !!(meeting && event && participant && !isLoadingMeeting);

	const { isGuest, isOther } = useCurrentUserRole(event ?? undefined);
	const meetingParticipant = isGuest ? event?.host : event?.guest;
	const showMeetingOptions = hasOneOnOne && meeting?.event && meetingParticipant && !isOther;
	const externalCalendarLink = meeting?.event?.externalCalendarLink;
	const hasExternalCalendar = showMeetingOptions && !!externalCalendarLink;
	const showInternalMeetingOptions = showMeetingOptions && !meeting.isPast() && !hasExternalCalendar && !meeting.isEventCanceled();
	const showEditSeries = showMeetingOptions && !meeting.isEventCanceled();

	// Disclosures
	const createMeetingModal = useDisclosure();
	const editSeriesModal = useDisclosure();
	const rescheduleModal = useDisclosure();
	const cancelMeetingModal = useDisclosure();
	const createReviewModal = useDisclosure();
	const { onOpen: onOpenGoalModal } = useCreateGoalModal();

	// Meeting frequency label (for edit series option)
	const getEventFrequencyLabel = () => {
		if (!meeting?.event) return '';

		const eventFrequency = meeting.event.frequency === 'biweekly' ? 'Bi-weekly' : meeting.event.frequency;
		const eventFrequencyFriendly = meeting.event.frequency === 'biweekly' ? 'Every other' : 'Every';
		const eventDayOfWeek = t(`one_on_ones:schedule_day_of_week_options.${meeting.event.scheduleDayOfWeek}`);

		const eventFrequencyDateText =
			eventFrequency === 'monthly'
				? t(`one_on_ones:meeting.modal.form.cadence.monthly_${meeting.event.scheduleOccurrenceInMonth}`, { day: eventDayOfWeek })
				: t('one_on_ones:frequency_label', { frequency: eventFrequencyFriendly, dayOfWeek: eventDayOfWeek });

		if (meeting.event.externalCalendarProvider) {
			const providerName = meeting.event.externalCalendarProvider === 'google-calendar' ? 'Google Calendar' : 'Outlook';
			return `Managed by ${providerName}`;
		}

		return meeting.event.startTime
			? t('one_on_ones:frequency_label_with_time', { frequencyLabel: eventFrequencyDateText, time: format(meeting.event.startTime, 'h:mma') })
			: eventFrequencyDateText;
	};

	// Calendar icon based on provider
	const getCalendarIcon = () => {
		if (meeting?.event?.externalCalendarProvider === 'google-calendar') return <GoogleCalendar />;
		if (meeting?.event?.externalCalendarProvider === 'outlook') return <Outlook />;
		return <CalendarOutline size="md" />;
	};

	// Handlers
	const handleShare = () => {
		if (event) {
			navigator.clipboard.writeText(event.shortcodeURL);
			toast({ title: t('common:copied') });
		}
	};

	const handleRecognize = () => {
		dispatch(GiveBoxEvent.Open, {
			recipient: { id: directReport.id, username: directReport.username },
		});
	};

	const handleGiveFeedback = () => {
		dispatch(FeedbackEvent.OpenUnsolicited, {
			defaultUsers: [
				{
					id: directReport.id,
					username: directReport.username,
				},
			],
		});
	};

	return (
		<>
			<HStack justifyContent="space-between" alignItems="center" w="100%" position="relative">
				<UserNavLink to={`/team/${directReport.id}`} isExpanded data-testid={`nav-link-view-${directReport.id}`}>
					<HStack spacing={rem(12)}>
						<Avatar name={directReport.displayName} src={directReport.profilePic} size="sm" />
						<Text fontSize={100} fontWeight="medium">
							{directReport.displayName}
						</Text>
					</HStack>
				</UserNavLink>

				{/* Actions menu - positioned outside the link for proper accessibility */}
				<Box position="absolute" right={rem(10)}>
					<Menu placement="right-start">
						<MenuButton
							as={IconButton}
							aria-label={t('navigation:teamMenu.menuLabel')}
							variant="ghost"
							size="sm"
							icon={<DotsHorizontalSolid size="sm" />}
							_hover={{ backgroundColor: 'secondary.bgAlpha' }}
							_active={{ backgroundColor: 'secondary.bgAlpha' }}
							_focusVisible={{ outline: '2px solid', outlineColor: 'blue.500', borderRadius: 'md', outlineOffset: '-2px' }}
						/>
						<Portal>
							<MenuList
								width="fit-content"
								zIndex={99999}
								sx={{
									'& > *:not(:last-child)': { mb: rem(4) },
									'& [role="menuitem"]': {
										minHeight: rem(40),
									},
									'& [role="menuitem"]:focus-visible': {
										outline: '2px solid',
										outlineColor: 'blue.500',
										outlineOffset: '-2px',
										borderRadius: 'sm',
										backgroundColor: 'bg.surface',
									},
								}}
							>
								{/* ONE-ON-ONES */}
								<ProtectedContent permissions={Permission.CanAccessOneOnOnes}>
									{/* Schedule 1:1 (when no existing meeting) */}
									{!hasOneOnOne && (
										<MenuItem onClick={createMeetingModal.onOpen}>
											<HStack gap={2}>
												<CalendarOutline />
												<Text>{t('navigation:teamMenu.scheduleOneOnOne')}</Text>
											</HStack>
										</MenuItem>
									)}

									{/* External calendar link */}
									{hasExternalCalendar && externalCalendarLink && (
										<MenuItem as="a" href={externalCalendarLink} target="_blank" rel="noopener noreferrer">
											<HStack gap={2} justifyContent="space-between" width="100%">
												<HStack gap={2}>
													{getCalendarIcon()}
													<Text>{t('one_on_ones:meeting.menu.viewOrEditEvent')}</Text>
												</HStack>
												<ExternalLinkOutline />
											</HStack>
										</MenuItem>
									)}

									{/* Connect calendar */}
									{showInternalMeetingOptions && (
										<MenuItem onClick={editSeriesModal.onOpen}>
											<HStack gap={2}>
												<PlugOutline />
												<Text>{t('one_on_ones:meeting.menu.connectCalendar')}</Text>
											</HStack>
										</MenuItem>
									)}

									{/* Reschedule */}
									{showInternalMeetingOptions && (
										<MenuItem onClick={rescheduleModal.onOpen}>
											<HStack gap={2}>
												<CalendarOutline />
												<Text>{t('one_on_ones:meeting.menu.reschedule_meeting')}</Text>
											</HStack>
										</MenuItem>
									)}

									{/* Cancel meeting */}
									{showInternalMeetingOptions && (
										<MenuItem onClick={cancelMeetingModal.onOpen}>
											<HStack gap={2}>
												<CalendarCancelledOutline />
												<Text>{t('one_on_ones:meeting.menu.cancel')}</Text>
											</HStack>
										</MenuItem>
									)}

									{/* Share meeting */}
									{showMeetingOptions && (
										<MenuItem onClick={handleShare}>
											<HStack gap={2}>
												<ShareOutline />
												<Text>{t('one_on_ones:meeting.menu.share')}</Text>
											</HStack>
										</MenuItem>
									)}

									{/* Divider before edit series (only when no external calendar) */}
									{showEditSeries && !hasExternalCalendar && <Divider />}

									{/* Edit series */}
									{showEditSeries && (
										<MenuItem onClick={editSeriesModal.onOpen}>
											<HStack gap={2}>
												<CogOutline />
												<VStack align="flex-start" spacing={0}>
													<Text>{t('one_on_ones:meeting.menu.edit')}</Text>
													<Text fontSize="sm" color="text.softest">
														{getEventFrequencyLabel()}
													</Text>
												</VStack>
											</HStack>
										</MenuItem>
									)}

									<Divider />
								</ProtectedContent>

								{/* Give recognition */}
								<ProtectedContent permissions={Permission.CanGiveRecognition}>
									<MenuItem onClick={handleRecognize}>
										<HStack gap={2}>
											<ClappingHandsOutline />
											<Text>{t('navigation:teamMenu.giveRecognition')}</Text>
										</HStack>
									</MenuItem>
								</ProtectedContent>

								{/* Create goal */}
								<ProtectedContent permissions={Permission.CanAccessGoals}>
									{canAccessGoals && (
										<MenuItem onClick={() => onOpenGoalModal(directReport.id)}>
											<HStack gap={2}>
												<TargetOutline />
												<Text>{t('navigation:teamMenu.createGoal')}</Text>
											</HStack>
										</MenuItem>
									)}
								</ProtectedContent>

								{/* Share feedback */}
								<ProtectedContent permissions={Permission.CanAccessOneOnOnes}>
									<MenuItem onClick={handleGiveFeedback}>
										<HStack gap={2}>
											<ChatAltOutline />
											<Text>{t('navigation:teamMenu.shareFeedback')}</Text>
										</HStack>
									</MenuItem>
								</ProtectedContent>

								{/* Create custom recap */}
								<ProtectedContent permissions={Permission.CanAccessOneOnOnes}>
									<MenuItem onClick={createReviewModal.onOpen}>
										<HStack gap={2}>
											<HistoryOutline />
											<Text>{t('navigation:teamMenu.createCustomRecap')}</Text>
										</HStack>
									</MenuItem>
								</ProtectedContent>
							</MenuList>
						</Portal>
					</Menu>
				</Box>
			</HStack>

			{/* Modals */}
			<CreateMeetingModal guestId={directReport.id} {...createMeetingModal} />
			{showMeetingOptions && meetingParticipant && (
				<>
					<EditMeetingModal eventId={meeting.eventId} guestId={meetingParticipant.id} {...editSeriesModal} />
					<RescheduleModal
						meeting={meeting}
						guest={meetingParticipant}
						isOpen={rescheduleModal.isOpen}
						onClose={rescheduleModal.onClose}
						onOpenCancelModal={cancelMeetingModal.onOpen}
					/>
					<CancelMeetingModal meeting={meeting} guestId={meetingParticipant.id} isOpen={cancelMeetingModal.isOpen} onClose={cancelMeetingModal.onClose} />
				</>
			)}
			<CreateReviewModal userId={directReport.id} {...createReviewModal} />
		</>
	);
};
